import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CONTENT } from "@/constants/contentTestIds";
import FieldSchemaEditor from "@/components/FieldSchemaEditor";

export default function ContentCategoryDetail() {
  const { brandId, categoryId } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("items");
  const [schemaDraft, setSchemaDraft] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [{ data: c }, { data: itms }] = await Promise.all([
        api.get(`/brands/${brandId}/content-categories/${categoryId}`),
        api.get(`/brands/${brandId}/content-categories/${categoryId}/items`),
      ]);
      setCat(c);
      setItems(itms);
      setSchemaDraft(c.fields || []);
      setNameDraft(c.name || "");
    } catch {
      setError("Category not found");
    }
  };
  useEffect(() => { load(); }, [categoryId]);

  const saveSchema = async () => {
    setError("");
    try {
      await api.put(`/brands/${brandId}/content-categories/${categoryId}`, {
        fields: schemaDraft,
      });
      setSavedAt(new Date().toISOString());
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save schema");
    }
  };

  const saveName = async () => {
    setError("");
    const next = nameDraft.trim();
    if (!next || next === cat.name) { setRenaming(false); return; }
    try {
      await api.put(`/brands/${brandId}/content-categories/${categoryId}`, { name: next });
      setRenaming(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Rename failed");
    }
  };

  const deleteCategory = async () => {
    setError("");
    try {
      await api.delete(`/brands/${brandId}/content-categories/${categoryId}`);
      navigate(`/brands/${brandId}/content`);
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
      setConfirmDelete(false);
    }
  };

  if (error && !cat) return <p className="text-[#B5462F]">{error}</p>;
  if (!cat) return <p className="text-[#6B6358]">Loading&hellip;</p>;

  const canDelete = items.length === 0;

  return (
    <div data-testid={CONTENT.catDetailRoot} className="max-w-5xl">
      <Link to={`/brands/${brandId}/content`} className="text-xs text-[#6B6358]">&larr; All categories</Link>
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mt-4 mb-2">Content category</p>

      <div className="flex flex-wrap items-baseline gap-4 justify-between">
        <div className="flex-1 min-w-[240px]">
          {!renaming ? (
            <h1
              data-testid="content-cat-name"
              className="font-serif text-5xl tracking-tight cursor-text"
              onClick={() => setRenaming(true)}
              title="Click to rename"
            >
              {cat.name}
            </h1>
          ) : (
            <div className="flex gap-2 items-center">
              <input
                autoFocus
                data-testid="content-cat-name-input"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") { setRenaming(false); setNameDraft(cat.name); }
                }}
                className="font-serif text-4xl tracking-tight px-2 py-1 border border-[#1A1F2C]/30 rounded bg-[#F8F5EE]"
              />
              <button
                data-testid="content-cat-name-save"
                onClick={saveName}
                className="px-3 py-1.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em]"
              >Save</button>
              <button
                onClick={() => { setRenaming(false); setNameDraft(cat.name); }}
                className="text-xs text-[#6B6358]"
              >Cancel</button>
            </div>
          )}
          <p className="font-mono text-xs text-[#6B6358] mt-1">{cat.slug}</p>
        </div>
        <button
          type="button"
          data-testid="content-cat-delete"
          onClick={() => canDelete && setConfirmDelete(true)}
          disabled={!canDelete}
          title={canDelete ? "Delete this category" : "Cannot delete — category still has items."}
          className={[
            "px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border",
            canDelete
              ? "border-[#B5462F]/40 text-[#B5462F]"
              : "border-[#1A1F2C]/15 text-[#6B6358] cursor-not-allowed opacity-60",
          ].join(" ")}
        >
          Delete category
        </button>
      </div>

      {error && <p className="text-sm text-[#B5462F] mt-2">{error}</p>}

      <div className="mt-8 flex gap-1 border-b border-[#1A1F2C]/10">
        <Tab testid={CONTENT.catItemsTab} active={tab === "items"} onClick={() => setTab("items")}>
          Items &middot; {items.length}
        </Tab>
        <Tab testid={CONTENT.catSchemaTab} active={tab === "schema"} onClick={() => setTab("schema")}>
          Schema &middot; {cat.fields?.length || 0} fields
        </Tab>
      </div>

      <div className="mt-8">
        {tab === "items" && (
          <ItemsTab brandId={brandId} categoryId={categoryId} items={items} onChanged={load} />
        )}
        {tab === "schema" && (
          <div>
            <FieldSchemaEditor fields={schemaDraft} onChange={setSchemaDraft} />
            <div className="flex items-center gap-3 mt-6">
              <button
                data-testid={CONTENT.catSchemaSave}
                onClick={saveSchema}
                className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium"
              >
                Save schema
              </button>
              {savedAt && <span className="text-xs text-[#6B6358]">Saved.</span>}
              {error && <span className="text-sm text-[#B5462F]">{error}</span>}
            </div>
            <p className="text-xs text-[#6B6358] mt-5">
              Editing the schema does not delete existing item values — fields you
              remove are no longer rendered but their values remain in the
              database in case you re-add the field.
            </p>
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={`Delete "${cat.name}"?`}
          message="This category will be permanently removed. Items must be deleted first."
          confirmLabel="Delete category"
          onConfirm={deleteCategory}
          onCancel={() => setConfirmDelete(false)}
          testid="content-cat-delete-confirm"
        />
      )}
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 bg-[#1A1F2C]/60 z-50 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-[#F8F5EE] rounded-lg max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-serif text-2xl tracking-tight">{title}</h3>
        <p className="text-sm text-[#3D3A35] mt-2">{message}</p>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6B6358]"
          >Cancel</button>
          <button
            data-testid={`${testid}-confirm`}
            onClick={onConfirm}
            className="px-4 py-2 bg-[#B5462F] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em]"
          >{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-sm font-medium border-b-2 ${
        active ? "border-[#B5462F] text-[#1A1F2C]" : "border-transparent text-[#6B6358] hover:text-[#1A1F2C]"
      }`}
    >
      {children}
    </button>
  );
}

function ItemsTab({ brandId, categoryId, items, onChanged }) {
  const [confirmId, setConfirmId] = useState(null);
  const [error, setError] = useState("");

  const remove = async (item_id) => {
    setError("");
    try {
      await api.delete(`/brands/${brandId}/content-items/${item_id}`);
      setConfirmId(null);
      onChanged?.();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    }
  };

  const target = items.find((i) => i.item_id === confirmId);
  return (
    <>
      <div className="flex justify-end mb-4">
        <Link
          to={`/brands/${brandId}/content/${categoryId}/items/new`}
          data-testid={CONTENT.catNewItemButton}
          className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm"
        >
          New item
        </Link>
      </div>
      {error && <p className="text-sm text-[#B5462F] mb-2">{error}</p>}
      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg divide-y divide-[#1A1F2C]/10">
        {items.length === 0 && (
          <div className="px-6 py-8 text-sm text-[#6B6358]">No items yet.</div>
        )}
        {items.map((it) => (
          <div
            key={it.item_id}
            data-testid={`${CONTENT.catItemRowPrefix}${it.item_id}`}
            className="px-6 py-4 flex items-center justify-between hover:bg-[#F8F5EE]"
          >
            <Link
              to={`/brands/${brandId}/content-items/${it.item_id}`}
              className="flex-1 min-w-0"
            >
              <div className="font-serif text-lg">{it.name}</div>
              <div className="text-xs text-[#6B6358] mt-1">
                Updated {new Date(it.updated_at).toLocaleString()}
              </div>
            </Link>
            <button
              type="button"
              data-testid={`content-item-delete-${it.item_id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmId(it.item_id); }}
              className="ml-3 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] border border-[#B5462F]/40 text-[#B5462F]"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
      {target && (
        <ConfirmModal
          title={`Delete "${target.name}"?`}
          message="This content item will be permanently removed. Any document that references it will keep its assembled copy intact, but future regenerates won't pull from this item."
          confirmLabel="Delete item"
          onConfirm={() => remove(target.item_id)}
          onCancel={() => setConfirmId(null)}
          testid={`content-item-delete-confirm-${target.item_id}`}
        />
      )}
    </>
  );
}
